'use client';

import { useEffect, useRef } from 'react';

type AdSize = 'banner' | 'rectangle' | 'leaderboard' | 'inarticle' | 'responsive';

interface AdSlotProps {
  /** AdSense slot ID (data-ad-slot value from AdSense UI) */
  slot: string;
  /** Ad format/size preset */
  size?: AdSize;
  /** Whether this is a fluid in-article native ad */
  format?: 'auto' | 'fluid';
  /** Extra className for the wrapper */
  className?: string;
  /** Label above the ad — required by AdSense TOS in some regions */
  showLabel?: boolean;
}

/**
 * Sizes are reserved via inline style to prevent CLS.
 * These match Google AdSense standard sizes.
 */
const SIZE_MAP: Record<AdSize, { minHeight: number; maxWidth: number }> = {
  banner: { minHeight: 90, maxWidth: 728 }, // desktop leaderboard variant
  rectangle: { minHeight: 250, maxWidth: 300 }, // medium rectangle
  leaderboard: { minHeight: 90, maxWidth: 728 },
  inarticle: { minHeight: 250, maxWidth: 728 },
  responsive: { minHeight: 250, maxWidth: 1200 },
};

/**
 * AdSlot renders a Google AdSense ad unit with reserved dimensions.
 *
 * CRITICAL: The wrapping <div> has a fixed min-height to prevent Cumulative Layout Shift (CLS).
 * Never render an AdSlot without reserved space — this hurts ranking AND revenue.
 *
 * Usage:
 *   <AdSlot slot="1234567890" size="rectangle" />
 */
export function AdSlot({
  slot,
  size = 'rectangle',
  format = 'auto',
  className = '',
  showLabel = true,
}: AdSlotProps) {
  const insRef = useRef<HTMLModElement>(null);
  const client = process.env.NEXT_PUBLIC_ADSENSE_CLIENT;

  useEffect(() => {
    if (!client || !insRef.current) return;
    try {
      // biome-ignore lint/suspicious/noExplicitAny: adsbygoogle is a global
      (window as any).adsbygoogle = (window as any).adsbygoogle || [];
      // biome-ignore lint/suspicious/noExplicitAny: adsbygoogle is a global
      (window as any).adsbygoogle.push({});
    } catch (err) {
      // Silently fail in dev / adblock scenarios
      console.debug('AdSense push failed', err);
    }
  }, [client]);

  const dims = SIZE_MAP[size];

  // In dev or when AdSense isn't configured, render a placeholder with reserved dimensions.
  if (!client) {
    return (
      <div
        className={`mx-auto my-6 flex items-center justify-center bg-muted text-xs text-muted-foreground ${className}`}
        style={{ minHeight: dims.minHeight, maxWidth: dims.maxWidth, width: '100%' }}
        aria-hidden="true"
      >
        Ad Slot ({size})
      </div>
    );
  }

  return (
    <div
      className={`mx-auto my-6 ${className}`}
      style={{ minHeight: dims.minHeight, maxWidth: dims.maxWidth, width: '100%' }}
    >
      {showLabel && (
        <div className="mb-1 text-center text-[10px] uppercase tracking-wider text-muted-foreground">
          Advertisement
        </div>
      )}
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block', minHeight: dims.minHeight }}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
}
